import { ArrowBackIcon } from '@/components/Icons';
import { useACPDuration, useACPForm } from '@/hooks/acps/index';
import ACPIcon from '@/assets/fhenix-acp-icon.svg';
import { NameSection } from '../components/NameSection';
import { ExpirySection } from '../components/ExpirySection';
import { FloatingButtonPage } from '@/components/CofheFloatingButton/pagesConfig/types';
import type { GenerateACPPageProps } from './types';
import { usePortalNavigation, usePortalToasts } from '@/stores';
import { PageContainer } from '@/components/CofheFloatingButton/components/PageContainer';
import { Button } from '@/components/CofheFloatingButton/components';

export const GenerateACPPage: React.FC<GenerateACPPageProps> = ({ onSuccessNavigateTo, onCancel, onBack, cause }) => {
  const { navigateBack, navigateTo, pageHistory } = usePortalNavigation();
  const { addToast } = usePortalToasts();

  const {
    acpName,
    error,
    nameError,
    isValid,
    isSubmitting,
    durationSeconds,
    handleNameChange,
    setDurationSeconds,
    handleSubmit,
  } = useACPForm({
    isDelegate: false,
    onSuccess: () => {
      // by default navigate to acps list, but if arrived here from elsewhere, go back where we came from
      onSuccessNavigateTo ? onSuccessNavigateTo() : navigateTo(FloatingButtonPage.ACPs);
      addToast({
        variant: 'success',
        title: 'ACP created',
      });
    },
    onError: (error) => {
      addToast({
        variant: 'error',
        title: 'Failed to create acp',
        description: error.message,
      });
    },
  });
  const { presets, units, customCount, customUnit, selectPreset, setCustomCount, setCustomUnit, applyCustom } =
    useACPDuration({ onDurationChange: setDurationSeconds, initialSeconds: durationSeconds });

  return (
    <PageContainer
      header={
        <button
          className="flex items-center gap-2 text-base font-semibold text-[#0E2F3F] transition-opacity hover:opacity-80 dark:text-white"
          type="button"
          onClick={onBack ?? navigateBack}
        >
          {(pageHistory.length > 0 || onBack) && <ArrowBackIcon fontSize="small" />}
          <span>Create Self-usage ACP</span>
        </button>
      }
      content={
        <div className="flex flex-col w-full gap-3">
          <div className="flex items-center gap-3 text-[#0E2F3F] dark:text-white">
            <div className="flex items-center justify-center rounded-lg border border-[#0E2F3F]/30 p-2 dark:border-white/40">
              <ACPIcon className="h-7 w-7 fill-inherit" aria-label="CoFHE ACP icon" />
            </div>
            <div className="text-lg font-semibold">CoFHE ACPs</div>
          </div>
          <p className="text-sm leading-relaxed text-[#355366] dark:text-white/80">
            {cause === 'clicked_on_confidential_balance' && (
              <>You are trying to view a confidential balance, which requires an ACP to access the encrypted data.</>
            )}
            ACPs are used to authenticate your identity when accessing encrypted data.
            <br />
            This form creates a "SELF" ACP, which only grants access to the signer's (your) data.
          </p>

          <NameSection acpName={acpName} error={nameError} onNameChange={handleNameChange} />

          <ExpirySection
            presets={presets}
            units={units}
            durationSeconds={durationSeconds}
            customCount={customCount}
            customUnit={customUnit}
            selectPreset={selectPreset}
            setCustomUnit={setCustomUnit}
            applyCustom={applyCustom}
          />
        </div>
      }
      footer={
        <div className="flex flex-col w-full gap-2">
          {error && (
            <div role="alert" className="text-error">
              {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Button onClick={onCancel ?? navigateBack}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!isValid || isSubmitting}
              aria-busy={isSubmitting}
              label={isSubmitting ? 'Creating...' : 'Create ACP'}
              onClick={handleSubmit}
            />
          </div>
        </div>
      }
    />
  );
};
